import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Phone, Menu, X } from "lucide-react";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`sticky-header ${isScrolled ? "scrolled glass" : "bg-transparent"}`}
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="/" 
            data-testid="logo"
            className="flex items-center gap-2"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">C</span>
            </div>
            <span className="font-heading text-xl font-bold text-slate-800">
              Cozy Nests
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button
              data-testid="nav-home"
              onClick={() => scrollToSection("hero")}
              className="text-slate-600 hover:text-sky-600 transition-colors font-medium"
            >
              Home
            </button>
            <button
              data-testid="nav-services"
              onClick={() => scrollToSection("services")}
              className="text-slate-600 hover:text-sky-600 transition-colors font-medium"
            >
              Services
            </button>
            <button
              data-testid="nav-about"
              onClick={() => scrollToSection("about")}
              className="text-slate-600 hover:text-sky-600 transition-colors font-medium"
            >
              About
            </button>
            <button
              data-testid="nav-contact"
              onClick={() => scrollToSection("booking")}
              className="text-slate-600 hover:text-sky-600 transition-colors font-medium"
            >
              Contact
            </button>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:+18888801344"
              data-testid="header-phone"
              className="flex items-center gap-2 text-slate-700 hover:text-sky-600 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium">+1-888-8801-344</span>
            </a>
            <Button
              data-testid="header-book-btn"
              onClick={() => scrollToSection("booking")}
              className="rounded-full px-6 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 shadow-lg shadow-sky-200 transition-all hover:-translate-y-0.5"
            >
              Book Appointment
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-slate-700"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden absolute top-20 left-0 right-0 bg-white border-t border-slate-100 shadow-lg animate-fade-in z-50"
          >
            <div className="flex flex-col p-4 space-y-2">
              <button
                data-testid="mobile-nav-home"
                onClick={() => scrollToSection("hero")}
                className="text-left py-3 px-2 text-slate-700 font-medium hover:bg-sky-50 rounded-lg transition-colors"
              >
                Home
              </button>
              <button
                data-testid="mobile-nav-services"
                onClick={() => scrollToSection("services")}
                className="text-left py-3 px-2 text-slate-700 font-medium hover:bg-sky-50 rounded-lg transition-colors"
              >
                Services
              </button>
              <button
                data-testid="mobile-nav-about"
                onClick={() => scrollToSection("about")}
                className="text-left py-3 px-2 text-slate-700 font-medium hover:bg-sky-50 rounded-lg transition-colors"
              >
                About
              </button>
              <button
                data-testid="mobile-nav-contact"
                onClick={() => scrollToSection("booking")}
                className="text-left py-3 px-2 text-slate-700 font-medium hover:bg-sky-50 rounded-lg transition-colors"
              >
                Contact
              </button>
              <div className="pt-4 border-t border-slate-100">
                <a
                  href="tel:+18888801344"
                  className="flex items-center gap-2 text-sky-600 font-medium mb-4"
                >
                  <Phone className="w-4 h-4" />
                  +1-888-8801-344
                </a>
                <Button
                  onClick={() => scrollToSection("booking")}
                  className="w-full rounded-full bg-gradient-to-r from-sky-500 to-blue-600"
                >
                  Book Appointment
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
